<template>
  <CRUDDialog
    :baseCrudKey="miscBaseCrudKey"
    @upsertItem="upsertTransaction"
  >
    <template v-slot:[`editedItem`]="{}">
      <TransactionEditedItem
        :baseCrudKey="miscBaseCrudKey"
        @source-updated="handleSourceUpdate"/>
    </template>
  </CRUDDialog>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';
import TransactionEditedItem from '../../Misc/Transaction/TransactionEditedItem.vue';
import { GL_CONSTANTS } from '../Transaction/gl-balances.util';
import CRUDDialog from '../../common/base/crud/CRUDDialog.vue';
import { toFiscalYear, createObjectFromArray } from '../../../util/shared/tmc-global';
import { amountToNumber } from '../../../util/shared/vue-global';
import { getWorkstationNumber } from '../../../util/workstation-util';
import { buildPaymentItem } from '../../Misc/Transaction/miscTransaction.util';
import isInterestLikeEntryType from './investmentEntryType.util';

export default {
  components: {
    CRUDDialog,
    TransactionEditedItem,
  },
  watch: {
    openMiscEditedItemFlag() {
      this.miscNewItem();
    },
  },
  props: {
    openMiscEditedItemFlag: {
      type: Number,
      default: 0,
    },
    baseCrudKey: {
      type: String,
      default: '',
    },
    interestReceived: {
      type: Number,
      default: 0.0,
    },
    investResult: {
      type: Object,
      default: () => ({}),
    },
    selectedItems: {
      type: Array,
      default: () => ([]),
    },
  },
  data: () => ({
    miscBaseCrudKey: 'miscTransaction',
    sourceId: '',
    customFundListObject: {},
    customUnitCodeListObject: {},
    interestAmount: 0,
  }),
  computed: {
    ...mapGetters(['todaysDate']),
    ...mapGetters('base/crud', [
      'items',
      'item',
      'isVisible',
      'criteria',
    ]),
    ...mapState({
      miscSettingItem: (state) => state.SystemConfig.miscSettingItem,
      fiscalYearFromState: (state) => state.glDaily.interestApportionment.fiscalYearForMiscReceipt,
    }),
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    isVisibleProp: {
      get() {
        return this.isVisible(this.miscBaseCrudKey);
      },
      set(value) {
        this.setIsVisible([this.miscBaseCrudKey, value]);
      },
    },
    editedItem: {
      get() {
        return this.item(this.miscBaseCrudKey);
      },
      set(value) {
        this.setItem([this.miscBaseCrudKey, value]);
      },
    },
    fundWiseInterestDetailsWithTaxUnit() {
      if (this.investResult && this.investResult) {
        return this.investResult;
      }
      return [];
    },
    isCriteriaAndMiscFyAreEqual() {
      return this.fiscalYearFromState === this.criteriaState.fiscalYear;
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setItem',
      'setIsVisible',
      'setCriteria',
    ]),
    ...mapActions(['flashSuccess']),
    ...mapActions('misc/miscTransaction', [
      'upsertMiscTrans',
      'loadLastReceiptNo',
      'loadLastTransactionNo',
    ]),
    ...mapActions('shared/fund', [
      'loadFunds',
    ]),
    ...mapActions('misc/schoolUnit', [
      'loadMiscUnitCode',
    ]),
    async miscNewItem() {
      if (this.isCriteriaAndMiscFyAreEqual) {
        // do nothing
      } else if (this.fiscalYearFromState) {
        // Fund Data Promise
        const fundListPromise = this.loadFunds({
          criteria: {
            fiscalYear: this.fiscalYearFromState,
          },
        });

        // UnitCode Data Promise
        const unitCodeListPromise = this.loadMiscUnitCode({
          criteria: {
            fiscalYear: this.fiscalYearFromState,
          },
        });

        // Fetch Data
        const [fundList, unitCodeList] = await Promise.all([
          fundListPromise,
          unitCodeListPromise,
        ]);

        // Convert Array into Object & Assign Data
        this.customFundListObject = createObjectFromArray(fundList, 'fund');
        this.customUnitCodeListObject = createObjectFromArray(unitCodeList, 'unitCodeNumber');
      }
      this.newItemDialog();
      this.$nextTick(() => {
        this.isVisibleProp = true;
      });
    },
    calculateTotalInterest(details) {
      const totalInterest = details.reduce((total, detail) => {
        if (isInterestLikeEntryType(detail.entryType)) {
          return total + detail.amount;
        }
        return total;
      }, 0);

      // Get description at index 0
      const description = details[0] ? details[0].description : null;

      return {
        totalInterest,
        description,
      };
    },
    buildMiscDetails() {
      const miscDetails = [];
      const resultInvestment = this.fundWiseInterestDetailsWithTaxUnit;
      const interestAmount = this.calculateTotalInterest(this.selectedItems);
      miscDetails.push({
        fund: ((resultInvestment || {}).fund || {})._id || null,
        source: this.sourceId || null,
        unit: null,
        taxDistrict: null,
        percentageBreakdown: 0,
        amount: interestAmount.totalInterest,
        taxUnitCode: null,
      });
      return {
        miscDetails,
      };
    },
    handleSourceUpdate(updatedSourceObj) {
      const foundObject = updatedSourceObj.find(
        (obj) => obj.sourceSubSourceNumber === GL_CONSTANTS.INVESTMENT_INTEREST_INCOME_SOURCE,
      );
      if (foundObject && (this.sourceId !== foundObject._id)) {
        this.sourceId = foundObject._id;
        this.newItemDialog();
      }
    },
    newItemDialog() {
      const interestAmount = this.calculateTotalInterest(this.selectedItems);
      const {
        miscDetails,
      } = this.buildMiscDetails();
      const investBank = (this.investResult || {}).bank || {};
      const newItem = {
        businessDate: this.todaysDate,
        fiscalYear: this.fiscalYearFromState || toFiscalYear(new Date(this.todaysDate)),
        isApportionLikeTax: false,
        receiptNumber: null,
        isCalculatePercentage: false,
        whomStr: investBank.bankName || null,
        description: interestAmount.description,
        // The misc Bank Alt Key list only holds banks with an alt key. A CD bank
        // usually has none, and pre-filling it would show its raw _id, so leave
        // the bank blank for the clerk to pick the deposit bank.
        bank: (investBank.altKeyBase && investBank._id) || null,
        amount: interestAmount.totalInterest,
        transactionNumber: null,
        details: miscDetails,
        wkst: getWorkstationNumber(),
        taxYear: this.miscSettingItem.taxYear,
        paymentDetails: [
          {
            amount: interestAmount.totalInterest,
            paymentType: 'EFT',
            eftType: 'OTHER',
          },
        ],
      };
      this.editedItem = { ...newItem };
      return newItem;
    },
    async upsertTransaction(item) {
      const upsertItem = { ...item };
      upsertItem.amount = amountToNumber(upsertItem.amount);
      upsertItem.details = upsertItem.details.map((element) => {
        const amount = amountToNumber(element.amount);
        return { ...element, amount };
      });
      if ((upsertItem.paymentDetails || []).length > 0) {
        const newPaymentDetails = [];
        for (let i = 0; i < upsertItem.paymentDetails.length; i += 1) {
          const element = upsertItem.paymentDetails[i];
          const newUpdatedElement = buildPaymentItem(element);
          // if paymentType is empty, do not push the paymentDetails
          if (newUpdatedElement.paymentType) {
            newPaymentDetails.push({
              ...newUpdatedElement,
            });
          }
        }
        upsertItem.paymentDetails = newPaymentDetails;
      }

      const response = await this.upsertMiscTrans({
        item: upsertItem,
      });
      /* After successfully upsert, get '_id' */
      if (response._id) {
        this.flashSuccess('Misc Receipt has Successfully Save!');
      }
    },
  },
};
</script>
